import React, { useState } from 'react';
import {
  IconButton, Badge, Popover, Box, Typography, List, ListItem,
  ListItemText, Divider, Button, Tooltip, Chip, CircularProgress,
  Stack, ListItemIcon
} from '@mui/material';
import {
  Notifications as NotifIcon,
  NotificationsNone as NotifEmptyIcon,
  DoneAll as DoneAllIcon,
  Delete as DeleteIcon,
  DeleteSweep as DeleteReadIcon,
  FiberManualRecord as UnreadDotIcon,
  ShoppingCart as PurchaseIcon,
  Inventory as InventoryIcon,
  AccountBalance as AccountingIcon,
  PeopleAlt as HRIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { NotificationDto } from '../../services/notifications.service';

const getNavigationPath = (entityType?: string, entityId?: number): string | null => {
  if (!entityType) return null;
  const routes: Record<string, string> = {
    'Absence': '/absences',
    'Deduction': '/deductions',
    'Warning': '/warnings',
    'DailyAttendance': '/daily-attendance',
    'Leave': '/leaves',
    'Payroll': '/payrolls',
    'Employee': '/employees',
    'PurchaseInvoice': '/purchase-invoices',
    'PurchaseOrder': '/purchase-orders',
    'PurchaseReturn': '/purchase-returns',
    'JournalEntry': '/journal-entries',
    'Voucher': '/vouchers',
    'StockIssue': '/stock-issues',
    'StockCount': '/stock-counts',
    'BranchTransfer': '/branch-transfers',
    'WarehouseTransfer': '/warehouse-transfers',
    'BranchIssueRequest': '/branch-issue-requests',
    'BranchIssue': '/branch-issues',
    'CustodySettlement': '/custody-settlements',
    'DailySale': '/daily-sales',
    'FixedAsset': '/fixed-assets',
    'FiscalPeriod': '/fiscal-periods',
  };
  return routes[entityType] || null;
};

const getCategoryIcon = (entityType?: string) => {
  if (!entityType) return <InfoIcon fontSize="small" />;
  if (entityType.includes('Purchase')) return <PurchaseIcon fontSize="small" />;
  if (entityType.includes('Stock')) return <InventoryIcon fontSize="small" />;
  if (entityType.includes('Journal') || entityType.includes('Payroll')) return <AccountingIcon fontSize="small" />;
  if (entityType.includes('Custody')) return <HRIcon fontSize="small" />;
  return <InfoIcon fontSize="small" />;
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'High': return '#f44336';
    case 'Low': return '#9e9e9e';
    default: return '#4facfe';
  }
};

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return date.toLocaleDateString('en-GB');
};

const NotificationBell: React.FC = () => {
  const { language } = useLanguage();
  const { mode } = useTheme();
  const isRTL = language === 'ar';
  const isDark = mode === 'dark';
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();

  const {
    notifications, unreadCount, loading,
    markAsRead, markAllAsRead, deleteNotification, deleteAllRead
  } = useNotifications();

  const open = Boolean(anchor);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchor(e.currentTarget);
  };

  const handleClose = () => setAnchor(null);

  const handleNotificationClick = async (n: NotificationDto) => {
    if (!n.isRead) await markAsRead(n.notificationId);
    const path = getNavigationPath(n.relatedEntityType, n.relatedEntityId);
    if (path) {
      handleClose();
      navigate(path);
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    await deleteNotification(id);
  };

  return (
    <>
      <Tooltip title={isRTL ? 'الإشعارات' : 'Notifications'}>
        <IconButton onClick={handleOpen} sx={{ color: 'white' }}>
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.65rem',
                minWidth: 18,
                height: 18,
                padding: '0 4px',
              }
            }}
          >
            {unreadCount > 0 ? <NotifIcon /> : <NotifEmptyIcon />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: isRTL ? 'left' : 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: isRTL ? 'left' : 'right' }}
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 520,
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
            background: isDark ? '#1e2a4a' : '#ffffff',
            display: 'flex',
            flexDirection: 'column',
          }
        }}
      >
        {/* Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          px: 2, py: 1.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <NotifIcon sx={{ color: 'white', fontSize: 22 }} />
            <Typography fontWeight={700} color="white" variant="subtitle1">
              {isRTL ? 'الإشعارات' : 'Notifications'}
            </Typography>
            {unreadCount > 0 && (
              <Chip
                label={unreadCount}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.3)', color: 'white', fontWeight: 700, fontSize: '0.75rem' }}
              />
            )}
          </Stack>
          <Stack direction="row" spacing={0.5}>
            {unreadCount > 0 && (
              <Tooltip title={isRTL ? 'تعليم الكل كمقروء' : 'Mark all as read'}>
                <IconButton size="small" onClick={markAllAsRead} sx={{ color: 'white' }}>
                  <DoneAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={isRTL ? 'حذف المقروءة' : 'Delete read'}>
              <IconButton size="small" onClick={deleteAllRead} sx={{ color: 'white' }}>
                <DeleteReadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Content */}
        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <NotifEmptyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary" variant="body2">
                {isRTL ? 'لا توجد إشعارات' : 'No notifications'}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {notifications.map((n, idx) => (
                <React.Fragment key={n.notificationId}>
                  <ListItem
                    alignItems="flex-start"
                    onClick={() => handleNotificationClick(n)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: n.isRead
                        ? 'transparent'
                        : isDark ? 'rgba(79,172,254,0.08)' : 'rgba(79,172,254,0.06)',
                      '&:hover': { bgcolor: isDark ? 'rgba(79,172,254,0.12)' : 'rgba(79,172,254,0.1)' },
                      pr: 1,
                      py: 1.5,
                    }}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    secondaryAction={
                      <Tooltip title={isRTL ? 'حذف' : 'Delete'}>
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={(e) => handleDelete(e, n.notificationId)}
                          sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                      <Box
                        sx={{
                          bgcolor: `${getPriorityColor(n.priority)}20`,
                          color: getPriorityColor(n.priority),
                          borderRadius: '50%',
                          width: 32, height: 32,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        {getCategoryIcon(n.relatedEntityType)}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          {!n.isRead && (
                            <UnreadDotIcon sx={{ fontSize: 8, color: '#4facfe', flexShrink: 0 }} />
                          )}
                          <Typography
                            variant="body2"
                            fontWeight={n.isRead ? 400 : 600}
                            color={isDark ? 'white' : 'text.primary'}
                            sx={{ lineHeight: 1.4 }}
                          >
                            {isRTL && n.title.includes(':')
                              ? n.title  // Backend sends bilingual title
                              : n.title}
                          </Typography>
                        </Stack>
                      }
                      secondary={
                        <Box component="span" sx={{ display: 'block' }}>
                          <Typography variant="caption" color="text.secondary" component="span" display="block" sx={{ mt: 0.25 }}>
                            {n.message}
                          </Typography>
                          <Typography variant="caption" color="text.disabled" component="span" display="block" sx={{ mt: 0.5 }}>
                            {formatDate(n.createdDate)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {idx < notifications.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default React.memo(NotificationBell);
